import { type MouseEvent as ReactMouseEvent, useMemo, useRef, useState } from 'react';

import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  parseDateRange,
  serializeDateRange,
} from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import { useTableFilters } from '@/components/data-table/filters-v2';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useDataTable } from '@/hooks/use-data-table';
import { useMeasuredHeight } from '@/hooks/use-measured-height';
import { usePersistedTableFilters } from '@/hooks/use-persisted-table-filters';
import { TableIds } from '@/lib/constants/table-ids';
import { PageTemplate, usePermissions } from '@/modules/common';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import {
  DateRangePreset,
  DateRangeV2,
  detectPresetFromDates,
  getDateRangeFromPreset,
} from '@/modules/common/components/molecules/date/date-range-picker-v2';
import {
  CollapsibleHeaderSection,
  useCollapseSpacer,
} from '@/modules/common/components/organisms/collapsible-header-section';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';

import {
  DeleteOverheadModal,
  OverheadFormModal,
  OverheadsRowContextMenu,
  OverheadsRowContextMenuState,
  OverheadsTable,
  OverheadsToolbar,
  useOverheadsSummaryRenditions,
} from '../components';
import { getOverheadsFilterConfig, getOverheadsTableColumns } from '../constants';
import {
  OVERHEADS_FILTER_KEY_PERIOD,
  OVERHEADS_SEARCH_FILTER_MIN_CHARACTERS,
  OVERHEADS_TABLE_FILTER_KEYS,
  OVERHEADS_TABLE_FILTERS_STORAGE_KEY,
} from '../constants/overheads-filter-keys';
import { useOverheads } from '../hooks';
import { useUpdateOverheadStatus } from '../hooks/use-update-overhead-status';
import { OverheadListItem, OverheadStatus } from '../types/overhead';

// Collapsing-toolbar heights (px). The cards block has a fixed row count so
// its height is hardcoded for first-frame stability, but the header's
// subtitle wraps to a variable number of lines depending on viewport width
// (e.g. up to ~6 lines on a narrow phone vs 2 on desktop), so its height IS
// measured at runtime — see `useMeasuredHeight` below — and the expanded
// height derives from it instead of a second hardcoded constant.
const SUMMARY_HEADER_FALLBACK_PX = 84; // used until the header is measured
const SUMMARY_CARDS_PX = 176; // cards block (~165, 3 rows) + slack (~11)
const SUMMARY_COLLAPSED_PX = 52; // chip strip
const TOOLBAR_FALLBACK_PX = 120; // used until the toolbar is measured

export const OverheadsPage = () => {
  const { t } = useTranslation(['authenticated.overheads', 'common']);

  const { hasPermission } = usePermissions();
  const isManageEnabled = hasPermission(OpsiumPolicy.OverheadsManage);
  const isDeleteEnabled = hasPermission(OpsiumPolicy.OverheadsDelete);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editOverhead, setEditOverhead] = useState<OverheadListItem | null>(null);
  const [deleteOverhead, setDeleteOverhead] = useState<OverheadListItem | null>(null);
  const [markPlannedOverhead, setMarkPlannedOverhead] = useState<OverheadListItem | null>(null);
  const [rowContextMenu, setRowContextMenu] = useState<OverheadsRowContextMenuState | null>(null);

  const openCreateModal = () => {
    setEditOverhead(null);
    setIsFormModalOpen(true);
  };

  const openEditModal = (overhead: OverheadListItem) => {
    setEditOverhead(overhead);
    setIsFormModalOpen(true);
  };

  const closeFormModal = () => {
    setIsFormModalOpen(false);
    setEditOverhead(null);
  };

  // Row click mirrors the pencil action; invoiced entries are locked from
  // editing, so clicking them does nothing.
  const handleRowClick = (overhead: OverheadListItem) => {
    if (!isManageEnabled || overhead.status === OverheadStatus.Invoiced) return;
    openEditModal(overhead);
  };

  const handleRowContextMenu = (overhead: OverheadListItem, event: ReactMouseEvent) => {
    event.preventDefault();
    setRowContextMenu({ x: event.clientX, y: event.clientY, overhead });
  };

  const statusMutation = useUpdateOverheadStatus({
    onSuccess: () => setMarkPlannedOverhead(null),
  });

  // Marking paid is a plain optimistic flip; marking back to planned usually
  // corrects a mistake, so it goes through a lightweight confirmation first.
  const handleToggleStatus = (overhead: OverheadListItem) => {
    if (overhead.status === OverheadStatus.Paid) {
      setMarkPlannedOverhead(overhead);
      return;
    }
    statusMutation.mutate({ overheadId: overhead.id, status: OverheadStatus.Paid });
  };

  const columns = useMemo(
    () =>
      getOverheadsTableColumns({
        onEdit: isManageEnabled ? openEditModal : undefined,
        onDelete: isDeleteEnabled ? setDeleteOverhead : undefined,
        onToggleStatus: isManageEnabled ? handleToggleStatus : undefined,
        isStatusUpdating: (overheadId: number) =>
          statusMutation.isPending && statusMutation.variables?.overheadId === overheadId,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isManageEnabled, isDeleteEnabled, statusMutation.isPending, statusMutation.variables]
  );

  const defaultPeriod = useMemo(
    () => serializeDateRange(getDateRangeFromPreset(DateRangePreset.ThisMonth)),
    []
  );

  usePersistedTableFilters({
    storageKey: OVERHEADS_TABLE_FILTERS_STORAGE_KEY,
    filterKeys: OVERHEADS_TABLE_FILTER_KEYS,
    defaultFilters: { [OVERHEADS_FILTER_KEY_PERIOD]: defaultPeriod },
  });

  const [searchParams] = useSearchParams();
  const filterConfig = useMemo(() => getOverheadsFilterConfig(), []);
  const filterState = useTableFilters(filterConfig);
  const appliedFilters = filterState.activeFilters;

  // The period is a standalone date range picker (not a dropdown filter), but
  // its value is persisted in the same URL key via the shared filter state.
  const { dates, selectedPreset } = useMemo(() => {
    const paramValue = searchParams.get(OVERHEADS_FILTER_KEY_PERIOD);
    const parsed = paramValue ? parseDateRange(paramValue) : null;
    return { dates: parsed, selectedPreset: parsed ? detectPresetFromDates(parsed) : null };
  }, [searchParams]);

  const handlePeriodChange = (newDates: DateRangeV2) => {
    filterState.setFilter(OVERHEADS_FILTER_KEY_PERIOD, serializeDateRange(newDates));
  };

  const handlePeriodPreset = (preset: DateRangePreset) => {
    handlePeriodChange(getDateRangeFromPreset(preset));
  };

  const handlePeriodReset = () => {
    filterState.clearFilter(OVERHEADS_FILTER_KEY_PERIOD);
  };

  const filtersWithPeriod = useMemo(() => {
    const period = searchParams.get(OVERHEADS_FILTER_KEY_PERIOD);
    return period ? { ...appliedFilters, [OVERHEADS_FILTER_KEY_PERIOD]: period } : appliedFilters;
  }, [searchParams, appliedFilters]);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useOverheads({
    filters: filtersWithPeriod,
  });

  const { expandedHeader, expandedBody, collapsed } = useOverheadsSummaryRenditions({
    filters: filtersWithPeriod,
  });

  const hasNonPeriodFiltersApplied = Object.entries(appliedFilters).some(
    ([filterKey, filterValue]) => filterKey !== OVERHEADS_FILTER_KEY_PERIOD && Boolean(filterValue)
  );
  const hasAppliedSearch = Boolean(
    appliedFilters.searchString &&
      typeof appliedFilters.searchString === 'string' &&
      appliedFilters.searchString.length >= OVERHEADS_SEARCH_FILTER_MIN_CHARACTERS
  );

  const overheadsCount = data?.pagination.total ?? 0;

  const { table, saveColumnSizing } = useDataTable<OverheadListItem>({
    data: data?.items ?? [],
    columns,
    getRowId: row => row.id.toString(),
    tableId: TableIds.OVERHEADS,
  });

  // The single page scroll container drives the summary collapse.
  const scrollRef = useRef<HTMLDivElement>(null);

  // The cards start below the header block; on narrow viewports the subtitle
  // wraps to more lines, so the header's height (and therefore the toolbar's
  // total expanded height) must be measured rather than hardcoded.
  // `collapsed` truthiness flips which branch renders the header wrapper
  // (`CollapsibleHeaderSection` vs. the static fallback), remounting the
  // measured node — pass it as `remountDep` so the observer re-attaches
  // instead of reporting the stale (detached => 0) height.
  const { ref: summaryHeaderRef, height: summaryHeaderPx } = useMeasuredHeight<HTMLDivElement>(
    SUMMARY_HEADER_FALLBACK_PX,
    !!collapsed
  );
  const summaryExpandedPx = summaryHeaderPx + SUMMARY_CARDS_PX;

  // The table column header pins exactly below the (measured) toolbar.
  const { ref: toolbarRef, height: toolbarHeight } =
    useMeasuredHeight<HTMLDivElement>(TOOLBAR_FALLBACK_PX);
  const tableHeaderTopPx = SUMMARY_COLLAPSED_PX + toolbarHeight;

  const needsCollapseSpacer = useCollapseSpacer({
    scrollRef,
    collapseDistancePx: summaryExpandedPx - SUMMARY_COLLAPSED_PX,
    contentDep: table.getRowModel().rows.length,
  });

  return (
    <PageTemplate
      title={t('title')}
      useStickyHeader
      className="p-0"
      headerClassName="items-start bg-surface px-4 pt-4 shadow-[0_1px_4px_0_hsl(var(--border))]"
      contentClassName="relative"
      rightActions={
        isManageEnabled ? (
          <Button size="sm" onClick={openCreateModal}>
            <CirclePlus className="h-4 w-4" />
            {t('addOverhead')}
          </Button>
        ) : undefined
      }
    >
      <Separator className="ml-[-16px] w-[calc(100%+32px)]" />

      {/* Single page scroll container. Sticky chrome stacks at the top:
          collapsing summary → toolbar → table column header. The table inside
          scrolls only horizontally. */}
      <div
        ref={scrollRef}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-4 pb-4 pt-4"
        style={{ overflowAnchor: 'none' }}
      >
        {collapsed ? (
          <CollapsibleHeaderSection
            expandedHeader={<div ref={summaryHeaderRef}>{expandedHeader}</div>}
            expandedBody={expandedBody}
            collapsed={collapsed}
            scrollContainerRef={scrollRef}
            expandedHeightPx={summaryExpandedPx}
            collapsedHeightPx={SUMMARY_COLLAPSED_PX}
            bodyOffsetPx={summaryHeaderPx}
            className="z-30 bg-background"
          />
        ) : (
          // No collapse while loading/error — render header + body statically.
          <div className="flex flex-col gap-3">
            <div ref={summaryHeaderRef}>{expandedHeader}</div>
            {expandedBody}
          </div>
        )}

        <OverheadsToolbar
          ref={toolbarRef}
          topPx={SUMMARY_COLLAPSED_PX}
          filtersConfig={filterConfig}
          filterState={filterState}
          dates={dates}
          selectedPreset={selectedPreset}
          onDateChange={handlePeriodChange}
          onPresetSelect={handlePeriodPreset}
          onDateReset={handlePeriodReset}
          table={table}
          counterText={t(`common.overheadsCounter.${hasAppliedSearch ? 'withSearch' : 'default'}`, {
            count: overheadsCount,
          })}
        />

        <OverheadsTable
          table={table}
          isLoading={isLoading || isFetchingNextPage}
          hasMore={hasNextPage}
          totalCount={overheadsCount}
          hasNonPeriodFiltersApplied={hasNonPeriodFiltersApplied}
          onLoadMore={fetchNextPage}
          onAddOverhead={openCreateModal}
          onResizeEnd={saveColumnSizing}
          onRowClick={handleRowClick}
          onRowContextMenu={handleRowContextMenu}
          stickyHeaderTopPx={tableHeaderTopPx}
        />

        <OverheadsRowContextMenu state={rowContextMenu} onClose={() => setRowContextMenu(null)} />

        {/* Guarantees enough scroll travel to fully collapse the summary even
            when the table is short. Suppressed when the content already
            overflows (otherwise it's dead space at the bottom). */}
        {needsCollapseSpacer && (
          <div
            aria-hidden
            className="shrink-0"
            style={{ height: summaryExpandedPx - SUMMARY_COLLAPSED_PX }}
          />
        )}

        <OverheadFormModal
          open={isFormModalOpen}
          overhead={editOverhead}
          onClose={closeFormModal}
        />
        <DeleteOverheadModal overhead={deleteOverhead} onClose={() => setDeleteOverhead(null)} />
        <ConfirmationModal
          title={t('modals.markPlanned.title')}
          message={t('modals.markPlanned.description', { name: markPlannedOverhead?.name ?? '' })}
          confirmText={t('modals.markPlanned.submit')}
          confirmVariant="default"
          open={!!markPlannedOverhead}
          isLoading={statusMutation.isPending}
          onOpen={() => {}}
          onClose={() => setMarkPlannedOverhead(null)}
          onConfirm={() => {
            if (!markPlannedOverhead) return;
            statusMutation.mutate({
              overheadId: markPlannedOverhead.id,
              status: OverheadStatus.Planned,
            });
          }}
        />
      </div>
    </PageTemplate>
  );
};
